import styled, { css } from 'styled-components';
import { glsp, themeVal, truncated } from '@devseed-ui/theme-provider';
import React, { useContext, createContext, useMemo } from 'react';
import T from 'prop-types';
import { createOverlineStyles } from '@devseed-ui/typography';
import { Button } from '@devseed-ui/button';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject$1, _templateObject2$1;

var renderDividerVariation = function renderDividerVariation(_ref) {
  var variation = _ref.variation;

  if (variation === 'light') {
    return css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n      background: transparent\n        linear-gradient(\n          90deg,\n          ", ",\n          ", "\n        )\n        50% / ", " auto no-repeat;\n    "])), themeVal('color.surface-100a'), themeVal('color.surface-100a'), themeVal('layout.border'));
  } // Default: variation === 'dark'


  return css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n    background: transparent\n      linear-gradient(\n        90deg,\n        ", ",\n        ", "\n      )\n      50% / ", " auto no-repeat;\n  "])), themeVal('color.base-200a'), themeVal('color.base-200a'), themeVal('layout.border'));
};

var VerticalDivider = /*#__PURE__*/styled.hr.withConfig({
  displayName: "vertical-divider__VerticalDivider",
  componentId: "sc-aok8iv-0"
})(["display:inline-flex;border:0;width:", ";height:", ";margin:0;", ""], glsp(1 / 2), glsp(), renderDividerVariation);

// global toolbar props, like the size.
// Context

var ToolbarContext = /*#__PURE__*/createContext({
  inToolbar: false
}); // Context provider

var ToolbarProvider = function ToolbarProvider(props) {
  var children = props.children,
      size = props.size;
  var contextValue = useMemo(function () {
    return {
      inToolbar: true,
      size: size
    };
  }, [size]);
  return /*#__PURE__*/React.createElement(ToolbarContext.Provider, {
    value: contextValue
  }, children);
};
ToolbarProvider.propTypes = {
  children: T.node,
  size: T.oneOf(['small', 'medium', 'large'])
}; // Context consumer

var useToolbar = function useToolbar() {
  // It should be possible to use the hook outside the toolbar to allow
  // component reusability.
  return useContext(ToolbarContext);
};
var ToolbarContextButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _useToolbar = useToolbar(),
      size = _useToolbar.size;
  /* eslint-disable-next-line react/prop-types */


  return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
    size: size || props.size,
    ref: ref
  }));
});
ToolbarContextButton.displayName = 'ToolbarContextButton';

var _templateObject, _templateObject2, _templateObject3, _templateObject4;

var _excluded = ["className", "children", "size"];

function ToolbarComponent(_ref) {
  var className = _ref.className,
      children = _ref.children,
      size = _ref.size,
      rest = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(ToolbarProvider, {
    size: size
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: className,
    role: "toolbar"
  }, rest), children));
}

ToolbarComponent.propTypes = {
  className: T.string,
  children: T.node,
  size: T.string
};

var setSpacingVariable = function setSpacingVariable(_ref2) {
  var size = _ref2.size;

  switch (size) {
    case 'small':
      return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n        --toolbar-spacing: ", ";\n      "])), glsp(0.125));

    case 'large':
      return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n        --toolbar-spacing: ", ";\n      "])), glsp(0.5));

    case 'xlarge':
      return css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n        --toolbar-spacing: ", ";\n      "])), glsp());
    // medium

    default:
      return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n        --toolbar-spacing: ", ";\n      "])), glsp(0.25));
  }
};

var Toolbar = /*#__PURE__*/styled(ToolbarComponent).withConfig({
  displayName: "toolbar__Toolbar",
  componentId: "sc-1dvjpky-0"
})(["display:flex;flex-flow:row nowrap;align-items:center;", " gap:var(--toolbar-spacing);"], setSpacingVariable);
var ToolbarGroup = /*#__PURE__*/styled.div.attrs(function (props) {
  return {
    role: props.role || 'group'
  };
}).withConfig({
  displayName: "toolbar__ToolbarGroup",
  componentId: "sc-1dvjpky-1"
})(["all:inherit;& + &{margin-left:calc(var(--toolbar-spacing) * 4);}"]);
var ToolbarLabel = /*#__PURE__*/styled.span.withConfig({
  displayName: "toolbar__ToolbarLabel",
  componentId: "sc-1dvjpky-2"
})(["", " ", " color:", ";"], createOverlineStyles(), truncated(), function (_ref3) {
  var variation = _ref3.variation;
  return variation === 'light' ? themeVal('color.surface') : themeVal('color.base-400');
});
var ToolbarButton = /*#__PURE__*/styled(ToolbarContextButton).withConfig({
  displayName: "toolbar__ToolbarButton",
  componentId: "sc-1dvjpky-3"
})([""]);
var ToolbarIconButton = /*#__PURE__*/styled(ToolbarButton).attrs({
  fitting: 'skinny'
}).withConfig({
  displayName: "toolbar__ToolbarIconButton",
  componentId: "sc-1dvjpky-4"
})([""]);

export { Toolbar, ToolbarButton, ToolbarGroup, ToolbarIconButton, ToolbarLabel, VerticalDivider };
//# sourceMappingURL=index.es.js.map
